import { MenuItem } from '@mui/material';
import Grid from '@mui/material/Grid';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import * as Yup from 'yup';

import { FormBodyAPI } from 'src/constants/fieldsTypes';
import { PortalConfigContext, useFeatureFlags } from 'src/context';
import { SuggestIconPicker } from 'src/features/SuggestIconPicker';
import { commonValidationSchemaItems } from 'src/hooks/useEditModule';
import { AppVisibilityConfigSetting } from 'src/legacy/components/app-visibility/AppVisibilityConfigSetting';
import { RemoveApp } from 'src/legacy/components/AppSetup/RemoveApp';
import { BaseDescription } from 'src/legacy/components/CustomizationForm/BaseDescription';
import BaseTypography from 'src/legacy/components/Text/BaseTypography';
import {
  BaseTextField,
  ResizableTextField,
} from 'src/legacy/components/TextField';
import { GraySmall } from 'src/theme/colors';
import { VisibilityScenario } from 'src/utils/app_visibility';

import { SettingsPageRow } from '..';
import SettingsToggleCard from '../SettingsToggleCard';

import { WelcomeMessageEditor } from './WelcomeMessageEditor';
import { WelcomeMessageSenderInput } from './WelcomeMessageSenderInput';

type MessageSettingsFormStyleProps = {
  allowSignUp: boolean;
};

const useStyles = makeStyles<MessageSettingsFormStyleProps>()(
  (theme, props) => ({
    container: {
      display: 'flex',
      alignItems: 'center',
    },
    helperText: {
      color: GraySmall,
    },
    iconPickerWrapper: {
      marginTop: theme.spacing(3.5),
      marginBottom: theme.spacing(3.5),
    },
    toggleWrapper: {
      paddingBottom: theme.spacing(7.5),
    },
    messageContentWrapper: {
      paddingTop: theme.spacing(3),
      paddingBottom: theme.spacing(3),
    },
    messageDescription: {
      paddingTop: theme.spacing(3),
      paddingBottom: theme.spacing(8),
    },
    messageContentTitle: {
      marginTop: props.allowSignUp ? theme.spacing(2) : 0,
    },
  }),
);

export const useMessagingSettingsForm = () => {
  const portalConfig = React.useContext(PortalConfigContext);
  const { EnableOtherMessagesEditor } = useFeatureFlags();
  const allowSignUp = !portalConfig.features.signupDisabled;
  const { t } = useTranslation();
  const { classes } = useStyles({ allowSignUp });
  const validationScheme = Yup.object().shape({
    ...commonValidationSchemaItems,
    fields: Yup.object().shape({
      messageBody: Yup.string().required('Message is required'),
      isSecure: Yup.boolean().required(),
      senderUserId: Yup.string().required('Sender is required'),
      isWelcomeMessageDisabled: Yup.boolean(),
    }),
  });

  const FormRenderer: React.FC<FormBodyAPI> = ({
    errors,
    handleChange,
    handleBlur,
    values,
    touched,
    setFieldValue,
  }) => (
    <>
      <BaseTextField
        autoFocus
        fullWidth
        label={
          <div className={classes.container}>
            <BaseTypography fontType="13Medium">Name</BaseTypography>
            <BaseTypography className={classes.helperText}>
              &nbsp;- How it should appear in the sidebar
            </BaseTypography>
          </div>
        }
        variant="outlined"
        sizeVariant="medium"
        name="label"
        value={values.label}
        onChange={handleChange}
        onBlur={handleBlur}
        error={Boolean(touched && touched.label && errors && errors.label)}
        helperText={touched && touched.label && errors && errors.label}
      />
      <div className={classes.iconPickerWrapper}>
        <BaseTypography fontType="13Medium">Sidebar icon</BaseTypography>
        <SuggestIconPicker
          value={values.icon}
          onChange={(iconName) => setFieldValue('icon', iconName)}
          appName={values.label}
        />
      </div>
      <Grid spacing={3} className={classes.toggleWrapper}>
        <SettingsToggleCard
          title="Email notifications contain message contents"
          description="When disabled, users have to log in to view message contents."
          fieldName="fields.isSecure"
          checked={values.fields.isSecure}
          invertBooleanValue
        />
      </Grid>
      <SettingsPageRow
        hideDivider
        description={
          <BaseDescription
            title={t(
              ($) => $.page_apps.messaging.settings.welcome_message.title,
            )}
            descriptions={[
              t(($) =>
                allowSignUp
                  ? $.page_apps.messaging.settings.welcome_message
                      .description_signup_allowed
                  : $.page_apps.messaging.settings.welcome_message.description,
              ),
            ]}
          />
        }
        loadingProps={{ minHeight: 0 }}
      >
        <Grid container className={classes.messageContentWrapper}>
          <BaseTextField
            fullWidth
            select
            variant="outlined"
            onChange={(event) => {
              handleChange(event);
              setFieldValue(
                'fields.isWelcomeMessageDisabled',
                event.target.value === 'off',
              );
            }}
            label=""
            onBlur={handleBlur}
            name="fields.isWelcomeMessageDisabled"
            defaultValue="on"
            value={values.fields.isWelcomeMessageDisabled ? 'off' : 'on'}
          >
            <MenuItem value="off">Off</MenuItem>
            <MenuItem value="on">On</MenuItem>
          </BaseTextField>
        </Grid>
        {!values.fields.isWelcomeMessageDisabled && (
          <div>
            {allowSignUp && (
              <div>
                <BaseTypography fontType="13Medium">Sender</BaseTypography>
                <WelcomeMessageSenderInput
                  width={268}
                  senderId={values.fields.senderUserId}
                  onInputChanged={(value) =>
                    setFieldValue('fields.senderUserId', value)
                  }
                />
              </div>
            )}
            <Grid container className={classes.messageContentWrapper}>
              <div className="w-full">
                <BaseTypography component="div">
                  <div>{`Hello {firstName},`}</div>
                </BaseTypography>
                {EnableOtherMessagesEditor ? (
                  <WelcomeMessageEditor
                    name="fields.messageBody"
                    value={values.fields.messageBody}
                    onChange={(markdown) =>
                      setFieldValue('fields.messageBody', markdown)
                    }
                    onBlur={handleBlur}
                    error={!!(errors.fields && errors.fields.messageBody)}
                    helperText={errors.fields && errors.fields.messageBody}
                  />
                ) : (
                  <ResizableTextField
                    helperText={errors.fields && errors.fields.messageBody}
                    error={!!(errors.fields && errors.fields.messageBody)}
                    fullWidth
                    name="fields.messageBody"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.fields.messageBody}
                    variant="outlined"
                  />
                )}
              </div>
            </Grid>
          </div>
        )}
        <AppVisibilityConfigSetting scenario={VisibilityScenario.EditApp} />
        <RemoveApp />
      </SettingsPageRow>
    </>
  );

  return { validationScheme, FormRenderer };
};
